import Button from '@monofront/design-system/components/Button';
import { formatDate } from '@monofront/business/utils';
import { Status, TrackingLink, CancelLink } from '@components/Order';
import { format as formatPrice } from '@monofront/business/utils';
import React from 'react';
import { IOrder, INoteAttribute } from '@monofront/types';
import RebuyCart from '@monofront/design-system/visuals/Icons/Old/RebuyCart';
import { isMobileApp } from '@utils/environment';
import { sendMessageToMobile } from '@utils/mobile';
import { useTranslations } from 'next-intl';
import InformativeBox from '@monofront/design-system/components/InformativeBox';
import { LateAddToCartBanner } from '@monofront/lateaddtocart-web';
import { useFeatureIsOn } from '@monofront/business/contexts';
import { ABTEST_LATE_ADD_TO_CART } from '@monofront/business/constants';

interface IOrderBodyMobileProps {
  currentOrder: IOrder;
  onReorder: () => void;
  showCancelModal: () => void;
  isCancellable: boolean;
}

const OrderBodyMobile: React.FC<IOrderBodyMobileProps> = ({
  currentOrder,
  onReorder,
  showCancelModal,
  isCancellable,
}) => {
  const { shippingAddress, createdAt, totalPrice, noteAttributes } =
    currentOrder;

  const invoiceUrl =
    noteAttributes.find(
      (noteAttribute: INoteAttribute) => noteAttribute.name === 'invoice-url',
    )?.value || '';

  const tAccount = useTranslations('Account');
  const tLatcBanner = useTranslations('Account.latcBanner');
  const isLatcEnabled = useFeatureIsOn(ABTEST_LATE_ADD_TO_CART);

  const orderDeliveryDate = new Date(
    currentOrder.noteAttributes.find((note) => note.name === 'delivery_date')
      ?.value ?? '',
  ).toLocaleDateString('fr');
  const orderDeliverySlot = currentOrder.noteAttributes
    .find((note) => note.name === 'delivery_time_slot')
    ?.value?.replace(/\s/g, '')
    .split('-');

  const updatedTimeSlot = currentOrder.updatedTimeSlot;
  const isTimeSlotVisible =
    (orderDeliverySlot && orderDeliverySlot.length > 0) ||
    updatedTimeSlot?.deliveryDate;

  return (
    <>
      <div className="info">
        <div>
          <p className="date tw-mb-1">
            <span>
              {tAccount('orderDate', { date: formatDate(createdAt) })}
            </span>
          </p>
          <div className="status">
            <Status order={currentOrder} />
          </div>
        </div>
        <div className="links">
          <p className="price">{formatPrice(totalPrice)}</p>
          {invoiceUrl.length > 0 && (
            <a
              href={invoiceUrl}
              className="invoice-link tw-lf-link-md"
              target="_blank"
              rel="noreferrer"
            >
              {tAccount('invoice')}
            </a>
          )}
          {isCancellable && (
            <div className="cancel-action">
              <CancelLink order={currentOrder} onClick={showCancelModal} />
            </div>
          )}
        </div>
      </div>
      {isLatcEnabled && isMobileApp && (
        <div className="tw-mb-4">
          <LateAddToCartBanner
            orderId={currentOrder.id}
            title={tLatcBanner('title')}
            subtitle={tLatcBanner('subtitle')}
            onPress={() =>
              sendMessageToMobile('openLateAddToCart', {
                orderId: currentOrder.id,
              })
            }
          />
        </div>
      )}
      {shippingAddress && (
        <>
          <div className="tw-mb-4 tw-h-px tw-bg-blue-200" />
          <p className="tw-mb-2 tw-font-semibold">
            {tAccount('deliveryAddress')}{' '}
          </p>
          <ul className="">
            <li data-cs-mask>{shippingAddress.name}</li>
            {shippingAddress.company && (
              <li data-cs-mask>{shippingAddress.company}</li>
            )}
            <li data-cs-mask>{shippingAddress.address1}</li>
            {shippingAddress.address2 && (
              <li data-cs-mask>{shippingAddress.address2}</li>
            )}
            <li data-cs-mask>
              {shippingAddress.zip} {shippingAddress.city}
            </li>
          </ul>
        </>
      )}

      <div className="cta mobile-only">
        <Button onClick={onReorder} block={false} hasMinWidth={false}>
          <RebuyCart />
          {tAccount('reorder')}
        </Button>
        <div className="tracking">
          <TrackingLink order={currentOrder} />
        </div>
      </div>

      {isTimeSlotVisible && (
        <>
          <div className="tw-my-4 tw-flex tw-flex-col tw-space-y-2">
            {updatedTimeSlot?.deliveryDate && (
              <p className="tw-font-semibold">{tAccount('chosenSlot')}</p>
            )}
            {orderDeliveryDate && !updatedTimeSlot?.deliveryDate && (
              <p className="tw-font-semibold">{tAccount('selectedSlot')}</p>
            )}
            <p>
              {tAccount('timeSlot', {
                date: updatedTimeSlot?.deliveryDate ?? orderDeliveryDate,
                startTime: updatedTimeSlot?.startTime ?? orderDeliverySlot?.[0],
                endTime: updatedTimeSlot?.endTime ?? orderDeliverySlot?.[1],
              })}
            </p>
          </div>
          {updatedTimeSlot?.deliveryDate && (
            <div className="tw-mb-4">
              <InformativeBox
                UNSAFE_className="tw-px-6 tw-py-4"
                backgroundColor="#FFE7DA"
              >
                <div className="flex tw-ml-3 tw-flex-col tw-space-y-1">
                  <p className="tw-font-semibold">
                    {tAccount('timeSlotUpdated')}
                  </p>
                  <p>
                    {tAccount('deliverySlot', {
                      date: updatedTimeSlot.deliveryDate,
                      startTime: updatedTimeSlot.startTime,
                      endTime: updatedTimeSlot.endTime,
                    })}
                  </p>
                </div>
              </InformativeBox>
            </div>
          )}
        </>
      )}

      <style jsx>{`
        .info,
        .savings,
        .address {
          display: flex;
          justify-content: space-between;
        }

        .info {
          margin-bottom: var(--space-4);
        }

        .savings {
          padding: var(--space-4) 0;
        }
        .savings-amount {
          color: var(--clr-secondary-1);
        }

        .address {
          padding: var(--space-4) 0;
          border-top: var(--border);
          border-bottom: var(--border);
        }

        .cta {
          display: flex;
          justify-content: space-around;
          align-items: center;
          padding: var(--space-2) var(--space-2);
          background-color: var(--clr-primary-5);
          bottom: ${isMobileApp ? 0 : `env(safe-area-inset-bottom)`};
          left: 0;
          position: fixed;
          right: 0;
          z-index: var(--zindex-fixed);
        }

        .cta > :global(button) {
          display: flex;
          font-size: var(--font-size-2);
          justify-content: center;
          align-items: center;
          margin-right: var(--space-2);
        }

        .cta > :global(button > svg) {
          margin-right: var(--space-2);
        }

        .links,
        .address-list {
          text-align: right;
        }

        .price {
          font-size: var(--font-size-3);
          font-weight: var(--font-weight-bold);
          margin-bottom: var(--space-1);
          font-family: var(--font-family-1);
        }

        @media (max-width: 800px) {
          :global(#main-footer) {
            padding-bottom: 3em;
          }
        }
      `}</style>
    </>
  );
};

export default OrderBodyMobile;
